// src/features/staff/pages/StaffDetailsPage/components/StaffTaskTracking.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Refresh as RefreshIcon,
  History as HistoryIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
// TODO: Setup date lcoalization in the format func
import { format } from 'date-fns';
import LocalizedDateDistance from '../../../../../i18n/LocalizedDateDistance';

// Hooks
import { useTaskTracking } from '../../../../tasks/hooks/useTaskTracking';
import { useNavigate } from "react-router-dom";
import StaffTabSection from './StaffTabSection';

export default function StaffTaskTracking({ staffMember }) {
  const { t } = useTranslation(['staff', 'tasks', 'common']);
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  
  const {
    staffActivity,
    loading,
    error,
    clearError,
    fetchStaffMemberActivity,
  } = useTaskTracking();

  // Load data
  useEffect(() => {
    if (staffMember?.staff_member_id) {
      loadActivityData();
    }
  }, [staffMember, page, pageSize]);

  const loadActivityData = useCallback(async () => {
    if (!staffMember?.staff_member_id) return;
    
    await fetchStaffMemberActivity(staffMember.staff_member_id, {
      limit: pageSize,
      offset: page * pageSize,
    });
  }, [staffMember, fetchStaffMemberActivity, page, pageSize]);

  const handleRefresh = useCallback(() => {
    loadActivityData();
  }, [loadActivityData]);

  // Tracking type configurations for icons and colors
  const getTrackingTypeConfig = (type) => {
    const configs = {
      creation: { icon: <AssignmentIcon fontSize="small" />, color: 'success' },
      state_change: { icon: <PlayArrowIcon fontSize="small" />, color: 'info' },
      assignment_change: { icon: <PersonIcon fontSize="small" />, color: 'secondary' },
      property_update: { icon: <EditIcon fontSize="small" />, color: 'warning' },
      completion: { icon: <CheckCircleIcon fontSize="small" />, color: 'success' },
    };
    return configs[type] || { icon: <EditIcon fontSize="small" />, color: 'default' };
  };

  // DataGrid columns
  const columns = [
    {
      field: 'created_at',
      headerName: t('staff:grid.columns.date'),
      width: 150,
      // One line keeps the rows short; how long ago is on hover
      renderCell: (params) => (
        <Tooltip title={<LocalizedDateDistance date={params.value} />}>
          <Typography variant="body2" noWrap>
            {format(new Date(params.value), 'dd.MM.yyyy HH:mm')}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'tracking_type',
      headerName: t('staff:grid.columns.action_type'),
      width: 150,
      renderCell: (params) => {
        const config = getTrackingTypeConfig(params.value);
        return (
          <Chip
            icon={config.icon}
            label={t(`tasks:tracking_types.${params.value}`)}
            size="small"
            color={config.color}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'description',
      headerName: t('staff:grid.columns.description'),
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: 1.5 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'state_change',
      headerName: t('staff:grid.columns.state_change'),
      width: 250,
      renderCell: (params) => {
        const { before_values, after_values } = params.row;
        if (before_values?.state && after_values?.state) {
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={before_values.state}
                size="small"
                variant="outlined"
                color="default"
              />
              <Typography variant="body2">→</Typography>
              <Chip
                label={after_values.state}
                size="small"
                variant="outlined"
                color="secondary"
              />
            </Box>
          );
        }
        return null;
      },
    },
    {
      field: 'task_id',
      headerName: "", //t('staff:grid.columns.task'),
      width: 70,
      renderCell: (params) => (
        <Tooltip title={t('staff:actions.view_task')}>
          <IconButton
            onClick={() => {
              // Navigate to task
              navigate(`/tasks/${params.value}`, { replace: true });
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Transform data for DataGrid
  const rows = staffActivity.map((entry, index) => ({
    id: entry.tracking_id || index,
    ...entry,
  }));

  if (!staffMember) {
    return null;
  }

  return (
    <StaffTabSection
      icon={HistoryIcon}
      title={t('staff:task_tracking.title')}
      subtitle={t('staff:task_tracking.activity_count', { count: rows.length })}
      fill
      actions={(
        <Tooltip title={t('common:buttons.refresh')}>
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      )}
    >

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* DataGrid */}
      <Box sx={{ flex: 1, minHeight: 300, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowsPerPageOptions={[5, 10, 25, 50]}
          page={page}
          onPageChange={setPage}
          loading={loading}
          disableSelectionOnClick
          autoHeight={false}
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-cell': {
              py: 1,
            },
            '& .MuiDataGrid-row': {
              minHeight: '60px !important',
            },
          }}
          localeText={{
            noRowsLabel: t('staff:task_tracking.no_activity'),
            MuiTablePagination: {
              labelRowsPerPage: t('common:rows_per_page'),
            },
          }}
        />
      </Box>

      {/* Summary footer */}
      {rows.length > 0 && (
        <Box mt={2} pt={2} borderTop={1} borderColor="divider">
          <Typography variant="body2" color="text.secondary">
            {t('staff:task_tracking.showing_activities', { 
              showing: rows.length,
              name: staffMember.name 
            })}
          </Typography>
        </Box>
      )}
    </StaffTabSection>
  );
}